import {
  ProjectArticle,
  ProjectGallery,
  ProjectHighlightCards,
  ProjectInlineImage,
  ProjectLearningGrid,
  ProjectNextSteps,
  ProjectProcessTimeline,
  ProjectToolPills,
} from "../ProjectArticle";
import { getProjectSummary } from "../projectIndex";

const project = getProjectSummary("2")!;

export default function GizmoAppMinigameCreation() {
  return (
    <ProjectArticle project={project}>
      <ProjectInlineImage
        src="/assets/images/projects/gizmo-common-stem-result.jpg"
        alt="Gizmo minigame screen showing the word stem as a common result"
        maxWidth="max-w-2xl"
        fit="contain"
      />

      <p>
        This project is a minigame idea for the Gizmo app. The player enters a
        word, and the game reacts by giving it a rarity, a short meaning, and a
        reward-style reveal. The player can also choose their own vibes, which
        are just fun colours. Some vibes require special words to unlock them.
        The players are given hints. The goal is to make learning new words feel
        more playful and rewarding, rather than like homework.
      </p>

      <section>
        <h2 className="mb-4">About Gizmo</h2>
        <p>
          Gizmo is a creative minigame/app maker designed for teens and kids.
          The special thing that makes it stand out from other game creation
          tools is that it uses AI to develop the game with the developer. All
          you need is a good idea and a good understanding of what people want.
          Gizmo is a app where it lets you browse through millions of minigames
          and you are able to like and comment on other peoples minigames.
        </p>
        <p>
          This minigame is one part of that idea. It focuses on words. the
          player tries a word, sees what rarity it gets, learns a quick meaning,
          and slowly unlocks more of the game&apos;s personality.
        </p>
      </section>

      <section>
        <h2 className="mb-4">The Main Game Loop</h2>
        <p>
          The player types a word, gets a rarity, and then sees a small reward
          card. I wanted the result to feel like opening something collectible,
          even though the player is still learning a word meaning.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          <GizmoScreenshot
            src="/assets/images/projects/gizmo-common-stem-result.jpg"
            alt="Gizmo result screen explaining the word stem"
            caption="A simple result: the word gets a rarity and a short meaning."
          />
          <GizmoScreenshot
            src="/assets/images/projects/gizmo-pancakes-vibe-result.jpg"
            alt="Gizmo pancakes vibe unlock screen"
            caption="Special words can unlock new vibes, which makes the game feel more collectible."
          />
        </div>
      </section>

      <section>
        <h2 className="mb-4">Play my minigames here</h2>
        <div className="bg-white rounded-2xl p-5 md:p-6 space-y-4">
          <p>
            You can try my minigames inside the Gizmo app. First, download
            Gizmo from{" "}
            <a
              href="https://gizmo.party/"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-revelBlue underline underline-offset-4"
            >
              gizmo.party
            </a>
            . Unfortunately, Gizmo is currently only available for iOS.
          </p>
          <p>
            After installing it, search for the username{" "}
            <span className="font-semibold text-stem-dark">randomdood .</span>{" "}
            to find my minigames.
          </p>
        </div>
      </section>

      <ProjectHighlightCards
        challenge={
          <ul className="space-y-3">
            <li>Making a word game feel fun instead of like homework.</li>
            <li>Has that unlocking mechanic to keep players engaged.</li>
            <li>
              Making the rarity reveal feel exciting and easy to understand.
            </li>
          </ul>
        }
        result={
          <ul className="space-y-3">
            <li>A minigame screen where users can enter a word.</li>
            <li>
              Players can select from a variety of fun color themes that feels
              collectible (vibes).
            </li>
            <li>A short meaning shown in a clear reward panel.</li>
          </ul>
        }
      />

      <ProjectProcessTimeline
        items={[
          {
            step: "Start with the word input",
            detail:
              "The game begins with a simple prompt: enter a word. This keeps the first action clear and fast.",
          },
          {
            step: "Add a rarity reveal",
            detail:
              "The result gives the word a rarity, such as common, so the game feels a bit like collecting items.",
          },
          {
            step: "Explain the word",
            detail:
              "The result panel also shows a short meaning, so the player still learns something from the minigame.",
          },
          {
            step: "Make the screen playful",
            detail:
              "Buttons, icons, color, personal vibes, and the reward panel help the minigame feel like an app experience rather than a worksheet.",
          },
        ]}
      />

      <section>
        <h2 className="mb-4">What Kept Players Coming Back</h2>
        <p>
          Once people started playing, I added achievements, updates, and small
          discoveries so the game had more reasons to return. The community part
          became more important than I expected.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          <GizmoScreenshot
            src="/assets/images/projects/gizmo-achievements-panel.jpg"
            alt="Gizmo achievements panel showing discovered rewards"
            caption="Achievements gave players targets to chase."
          />
          <GizmoScreenshot
            src="/assets/images/projects/gizmo-updates-panel.jpg"
            alt="Gizmo updates panel listing new changes and milestones"
            caption="Updates helped explain what changed and made the game feel alive."
          />
          <GizmoScreenshot
            src="/assets/images/projects/gizmo-profile-stats.jpg"
            alt="Gizmo profile page for randomdood showing followers"
            caption="The profile page showed that a small community was starting to form."
            fit="cover"
          />
        </div>
      </section>

      <ProjectLearningGrid
        items={[
          "Small games need very clear rules.",
          "A simple reward can make learning feel more fun.",
          "The screen has to be readable before it can be exciting.",
          "Simplicity is key to a good user experience.",
        ]}
      />

      <ProjectToolPills
        tools={[
          "Gizmo app",
          "Minigame design",
          "Word meanings",
          "Rarity system",
          "UI layout",
          "AI prompting",
        ]}
      />

      <ProjectGallery
        images={[
          {
            src: "/assets/images/projects/gizmo-vibes-normal.jpg",
            alt: "Gizmo vibes panel showing the normal vibe",
            fit: "contain",
          },
          {
            src: "/assets/images/projects/gizmo-common-cat-result.jpg",
            alt: "Gizmo result screen for the word cat",
            fit: "contain",
          },
          {
            src: "/assets/images/projects/gizmo-pancakes-vibe-result.jpg",
            alt: "Gizmo pancakes vibe unlock result",
            fit: "contain",
          },
          {
            src: "/assets/images/projects/gizmo-achievements-panel.jpg",
            alt: "Gizmo achievements panel",
            fit: "contain",
          },
          {
            src: "/assets/images/projects/gizmo-updates-panel.jpg",
            alt: "Gizmo updates panel",
            fit: "contain",
          },
          {
            src: "/assets/images/projects/gizmo-profile-stats.jpg",
            alt: "Gizmo creator profile stats",
            fit: "contain",
          },
        ]}
      />

      <ProjectNextSteps
        title="In the End..."
        items={[
          {
            title: "Did i succeed?",
            detail:
              "Yes, I managed to get 3.6k likes and 239k plays on the Gizmo app!",
          },
          {
            title:
              "What would you advice developers trying to make a minigame like this?",
            detail:
              "Keep the gameplay simple and focused, don't overcomplicate it.",
          },
          {
            title:
              "What would you do next if you continued improving this project?",
            detail:
              "Add more variety to the results, like fun facts, jokes, or images related to the word. I would also want to build a stronger community around the minigame.",
          },
          {
            title:
              "What was the most important lesson you learned from this project?",
            detail:
              "The most important lesson was that a community around a game can make all the difference. When I first reached 80k plays, I noticed that people were helping each other with unlocking new vibes and sharing tips. That sense of shared discovery kept the game alive and growing way more than I expected.",
          },
        ]}
      />
    </ProjectArticle>
  );
}

function GizmoScreenshot({
  src,
  alt,
  caption,
  fit = "contain",
}: {
  src: string;
  alt: string;
  caption: string;
  fit?: "cover" | "contain";
}) {
  return (
    <figure className="bg-white rounded-2xl border border-stem-sand overflow-hidden">
      <div className="h-[420px] md:h-[520px] bg-stem-dark/5 flex items-center justify-center">
        <img
          src={src}
          alt={alt}
          className={`w-full h-full object-center ${
            fit === "cover" ? "object-cover" : "object-contain"
          }`}
        />
      </div>
      <figcaption className="p-4 text-sm text-stem-gray leading-relaxed">
        {caption}
      </figcaption>
    </figure>
  );
}
